import { Box, Column, Heading, Row, SeparatorLine, Text } from '@stenajs-webui/core';
import { Banner, PrimaryButton } from '@stenajs-webui/elements';
import { CenterModal, CenterModalProps, ModalHeader } from '@stenajs-webui/modal';
import React, { FC } from 'react';

interface Props extends CenterModalProps {
  onRequestClose?: () => void;
}

interface ClaimLevel {
  title: string;
  background: string;
  subtitle: string;
  description: string;
  bullets: string[];
}

const levels: ClaimLevel[] = [
  {
    title: 'LEVEL 1',
    background: '#dff2e1',
    subtitle: 'Standard claim',
    description: 'Straightforward claim review',
    bullets: [
      'Complete documents received.',
      'Damage / loss appears limited in value.',
      'Handled through normal claims process.',
      'Decision based on liability, evidence and quantum.'
    ]
  },
  {
    title: 'LEVEL 2',
    background: '#fff0cc',
    subtitle: 'Major / escalated insurance claim',
    description: 'Higher value or complex matter',
    bullets: [
      'High value, disputed facts or wider incident.',
      'May require surveyor, management or P&I / insurer input.',
      'No settlement position before internal authority checks.',
      'Additional evidence may be requested.'
    ]
  },
  {
    title: 'LEVEL 3',
    background: '#fde2e2',
    subtitle: 'Limited / legal regime claim (SDR)',
    description: 'Terms, conventions or SDR may apply',
    bullets: [
      'Liability may be excluded, reduced, time-barred or limited.',
      'SDR, weight, package, unit or contractual limits may apply.',
      'Legal / insurer alignment may be required.',
      'Final position follows applicable terms and law.'
    ]
  }
];

const claimantInfo: Array<{ info: string; why: string }> = [
  {
    info: 'Booking / shipment reference, route, sailing date, unit or trailer number',
    why: 'Identifies the shipment, operational records and relevant contract.'
  },
  {
    info: 'Clear description of alleged loss or damage, including date, place and discovery moment',
    why: 'Allows assessment of where and when the issue may have occurred.'
  },
  {
    info: 'Photos, delivery notes, inspection reports, invoices, repair estimates and survey reports',
    why: 'Supports liability, causation, quantum and reasonableness of the amount claimed.'
  },
  {
    info: 'Proof of entitlement to claim, authority, ownership, subrogation or representation',
    why: 'Confirms that the claimant is entitled to present or recover the claim.'
  },
  {
    info: 'Verified gross weight, package count and declared value where relevant',
    why: 'May be required for SDR, weight-based, package-based or contractual limitation calculations.'
  }
];

const reviewSteps: string[] = ['Claim received', 'Documents checked', 'Handling level assigned', 'Evidence / liability review', 'Position communicated'];

const ClaimInfoDialog: FC<Props> = (props) => {
  const { onRequestClose, ...modalProps } = props;

  return (
    <CenterModal {...modalProps} onRequestClose={onRequestClose}>
      <ModalHeader headerText="Freight Claims Customer Information Sheet" onRequestClose={onRequestClose} />
      <Box style={{ width: 'min(900px, 94vw)', maxHeight: '75vh', overflowY: 'auto' }}>
        <Column gap={2} indent={3} spacing={3}>
          <Banner
            variant="info"
            headerText="Important"
            text="This sheet is for guidance only and is provided without admission of liability. Every claim is reviewed against the evidence, the contract of carriage, Stena Line Conditions of Carriage, applicable conventions, mandatory law, insurer instructions, and all available rights, defences, exclusions, limitations and time bars."
          />

          <SeparatorLine />

          <Heading variant="h3">How Stena Line Handles Freight Claims</Heading>
          <Text style={{ fontStyle: 'italic' }}>
            The claim handling level depends on value, complexity, evidence, insurance involvement and the applicable terms or legal regime.
          </Text>

          <Row gap={2} style={{ flexWrap: 'wrap' }}>
            {levels.map((level) => (
              <Box key={level.title} style={{ flex: '1 1 240px', minWidth: 0 }} background={level.background} borderRadius="4px" indent={2} spacing={2}>
                <Column gap={1}>
                  <Text variant="bold" size="large">
                    {level.title}
                  </Text>
                  <Text variant="bold">{level.subtitle}</Text>
                  <Text size="small" style={{ fontStyle: 'italic' }}>
                    {level.description}
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {level.bullets.map((bullet) => (
                      <li key={bullet}>
                        <Text size="small">{bullet}</Text>
                      </li>
                    ))}
                  </ul>
                </Column>
              </Box>
            ))}
          </Row>

          <SeparatorLine />

          <Heading variant="h3">What the claimant must provide</Heading>
          <Column border="1px solid #d8d8d8" borderRadius="4px" style={{ overflow: 'hidden' }}>
            <Row background="#f2f2f2" indent={2} spacing={2} gap={2}>
              <Text variant="bold" size="small" style={{ flex: '1 1 45%' }}>
                Minimum claim information
              </Text>
              <Text variant="bold" size="small" style={{ flex: '1 1 55%' }}>
                Why this is required
              </Text>
            </Row>
            {claimantInfo.map((row, index) => (
              <Row key={index} indent={2} spacing={2} gap={2} borderTop="1px solid #d8d8d8">
                <Text size="small" style={{ flex: '1 1 45%' }}>
                  {row.info}
                </Text>
                <Text size="small" style={{ flex: '1 1 55%' }}>
                  {row.why}
                </Text>
              </Row>
            ))}
          </Column>

          <SeparatorLine />

          <Heading variant="h3">Claim review path</Heading>
          <Row gap={1} style={{ flexWrap: 'wrap' }}>
            {reviewSteps.map((step, index) => (
              <Box key={step} style={{ flex: '1 1 140px', minWidth: 0 }} background="#f2f2f2" borderRadius="4px" indent={2} spacing={2}>
                <Column gap={1}>
                  <Text variant="bold" size="large">
                    {index + 1}
                  </Text>
                  <Text size="small">{step}</Text>
                </Column>
              </Box>
            ))}
          </Row>

          <Banner
            variant="warning"
            text="Incomplete evidence may delay assessment. Do not repair, dispose of goods or remove packaging before photos and inspection evidence are preserved, unless immediate action is necessary to prevent further loss."
          />

          <Box style={{ textAlign: 'right' }}>
            <PrimaryButton label="Close" onClick={onRequestClose} />
          </Box>
        </Column>
      </Box>
    </CenterModal>
  );
};

export default ClaimInfoDialog;
